import React from "react";

/**
 * Flatten tree to a list of {node, depth, hasChildren, siblingIndex, siblingTotal} via DFS.
 */
function flattenTree(roots, nodesByParent, collapsed, depth, acc) {
  for (let i = 0; i < roots.length; i++) {
    const n = roots[i];
    const children = nodesByParent[n.id] || [];
    const hasChildren = children.length > 0;
    acc.push({ node: n, depth, hasChildren, siblingIndex: i, siblingTotal: roots.length });
    if (hasChildren && !collapsed[n.id]) {
      flattenTree(children, nodesByParent, collapsed, depth + 1, acc);
    }
  }
  return acc;
}

export default function OrgTree({ nodesByParent, levels, collapsed, onToggle, onAdd, onEdit, onDelete, onMove }) {
  const roots = nodesByParent.root || [];
  const flat = flattenTree(roots, nodesByParent, collapsed, 0, []);

  return (
    <ul className="space-y-0.5">
      {flat.map(({ node, depth, hasChildren, siblingIndex, siblingTotal }) => {
        const levelDef = levels.find(l => l.number === node.level_number);
        const nextLevel = levels.find(l => l.number > node.level_number);
        const isOpen = !collapsed[node.id];
        const canUp = siblingIndex > 0;
        const canDown = siblingIndex < siblingTotal - 1;
        return (
          <li key={node.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-neutral-50 group" style={{ paddingLeft: depth * 20 + 4 }} data-testid={"tree-node-" + node.id}>
            {hasChildren ? (
              <button onClick={() => onToggle(node.id)} className="text-neutral-400 hover:text-neutral-700 shrink-0 w-4 text-sm font-bold" data-testid={"toggle-" + node.id}>
                {isOpen ? "▾" : "▸"}
              </button>
            ) : (
              <span className="w-4 shrink-0" />
            )}
            <span className="font-mono text-[10px] text-neutral-400 shrink-0">L{node.level_number}</span>
            <span className="text-xs text-neutral-500 shrink-0">{levelDef?.label}:</span>
            <span className="font-semibold text-neutral-800 truncate">{node.label}</span>
            {node.ref_meta?.tax_id && <span className="text-xs text-neutral-400">({node.ref_meta.tax_id})</span>}
            {node.ref_meta?.code && <span className="text-xs text-neutral-400">[{node.ref_meta.code}]</span>}
            <div className="ml-auto flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity items-center">
              <div className="flex items-center border border-neutral-200 rounded bg-white">
                <button
                  onClick={() => onMove(node, "up")}
                  disabled={!canUp}
                  className="px-1.5 py-0.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-bold"
                  title="Subir"
                  data-testid={"move-up-" + node.id}
                >↑</button>
                <span className="w-px h-4 bg-neutral-200"/>
                <button
                  onClick={() => onMove(node, "down")}
                  disabled={!canDown}
                  className="px-1.5 py-0.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-bold"
                  title="Bajar"
                  data-testid={"move-down-" + node.id}
                >↓</button>
              </div>
              {nextLevel && (
                <button onClick={() => onAdd(nextLevel.number, node.id, node.label)} className="text-xs text-blue-600 hover:text-blue-800 px-2 font-semibold" data-testid={"add-child-" + node.id}>
                  + {nextLevel.label}
                </button>
              )}
              <button onClick={() => onEdit(node)} className="text-xs text-amber-600 hover:text-amber-800 px-1 font-semibold" data-testid={"edit-node-" + node.id}>
                Editar
              </button>
              <button onClick={() => onDelete(node)} className="text-rose-500 hover:text-rose-700 px-1 text-xs font-semibold" data-testid={"delete-node-" + node.id}>
                Eliminar
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
